/**
 * SPRINT 2 — TreatmentPlanReadOnly
 * ================================
 * Affichage en lecture seule d'un plan de traitement actif. Utilisé par
 * `HdjProtocolEditor` (lecture obligatoire du plan depuis /treatment-plan
 * qui encapsule le fallback legacy côté backend).
 *
 * Props:
 *   plan            : objet plan (peut avoir isLegacyView=true)
 *   onEditRedirect  : callback pour rediriger vers ClinicalEvaluationForm
 *
 * Si plan.isLegacyView=true → bandeau orange "Plan au format historique".
 * Si plan null → carte vide "Aucun plan actif".
 */
import { AlertTriangle, FileText, Edit3 } from "lucide-react";

const CATEGORY_LABELS = {
  ANTIEMETIC: "Anti-émétique",
  GCSF: "G-CSF",
  EPO: "EPO",
  BISPHOSPHONATE: "Bisphosphonate",
  DECAPEPTYL: "Decapeptyl",
  CORTICOID: "Corticoïde",
  HYDRATATION: "Hydratation",
  OTHER: "Autre",
};

export default function TreatmentPlanReadOnly({ plan, onEditRedirect }) {
  if (!plan) {
    return (
      <div
        className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center"
        data-testid="treatment-plan-empty"
      >
        <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        <p className="text-sm text-slate-600 font-medium">Aucun plan de traitement actif</p>
        <p className="text-xs text-slate-400 mt-1">
          Créez le plan depuis le dossier clinique pour administrer la chimio.
        </p>
      </div>
    );
  }

  const mp = plan.mainProtocol;
  const isLegacy = plan.isLegacyView === true;
  const sc = plan.surfaceArea?.value;

  return (
    <div className="space-y-3" data-testid="treatment-plan-readonly">
      {isLegacy && (
        <div
          className="rounded-md border border-orange-300 bg-orange-50 px-3 py-2 text-sm text-orange-800"
          data-testid="legacy-banner"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              <strong>Plan au format historique.</strong> Les molécules sont affichées
              depuis l'ancienne évaluation clinique. Pour formaliser le plan structuré,
              modifiez-le depuis le dossier clinique.
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        {/* En-tête protocole */}
        {mp ? (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-slate-500">Protocole principal</div>
                <h4 className="text-base font-semibold text-slate-900 truncate" data-testid="plan-main-label">
                  {mp.label || "—"}
                </h4>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500 mt-1">
                  {mp.referenceCode && mp.referenceCode !== "LEGACY" && (
                    <span className="font-mono">{mp.referenceCode}</span>
                  )}
                  {mp.therapeuticLine && <span>Ligne : {mp.therapeuticLine}</span>}
                  {mp.cycle?.frequency && <span>Cycle : {mp.cycle.frequency}</span>}
                  {mp.administrationRoute && <span>Voie : {mp.administrationRoute}</span>}
                  {sc != null && <span>SC : {sc} m²</span>}
                </div>
              </div>
              {onEditRedirect && (
                <button
                  type="button"
                  onClick={onEditRedirect}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 flex-shrink-0"
                  data-testid="plan-edit-redirect"
                >
                  <Edit3 className="w-3 h-3" /> Modifier
                </button>
              )}
            </div>

            {/* Composants */}
            {mp.components?.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm" data-testid="plan-components-list">
                {mp.components.map((c, i) => (
                  <li key={i} className="flex flex-wrap items-baseline gap-x-2 text-slate-700">
                    <span className="font-medium">{c.dci}</span>
                    {c.doseFormula && <span className="text-slate-500">{c.doseFormula}</span>}
                    {c.route && <span className="text-slate-400 text-xs">{c.route}</span>}
                    {c.days && <span className="text-slate-400 text-xs">· {c.days}</span>}
                    {c.biosimilarChosen && (
                      <span className="text-xs text-blue-600" data-testid={`component-biosimilar-${i}`}>
                        ({c.biosimilarChosen})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {/* FreeNote (nuance locale — compact) */}
            {mp.freeNote && (
              <div className="mt-3 rounded bg-amber-50 border-l-2 border-amber-300 px-2 py-1.5 text-xs text-amber-900">
                <span className="font-medium">Note : </span>{mp.freeNote}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-slate-500 italic">
            Pas de protocole principal (patient sous surveillance / hormonothérapie seule).
          </div>
        )}

        {/* Complémentaires */}
        {plan.complementaryMeds?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-100">
            <div className="text-xs text-slate-500 mb-1.5">Médicaments complémentaires</div>
            <ul className="space-y-0.5 text-sm" data-testid="plan-complementary-list">
              {plan.complementaryMeds.map((m, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="inline-block text-[10px] uppercase tracking-wide text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                    {CATEGORY_LABELS[m.category] || m.category}
                  </span>
                  <span className="font-medium text-slate-700">{m.dci}</span>
                  {m.doseFormula && <span className="text-slate-500 text-xs">{m.doseFormula}</span>}
                  {m.route && <span className="text-slate-400 text-xs">{m.route}</span>}
                  {m.timing && <span className="text-slate-400 text-xs">· {m.timing}</span>}
                  {m.biosimilarChosen && <span className="text-xs text-blue-600">({m.biosimilarChosen})</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Métadonnées de création */}
        {plan.createdAt && (
          <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400">
            Plan créé par {plan.createdByName || "—"} le {new Date(plan.createdAt).toLocaleDateString("fr-FR")}
          </div>
        )}
      </div>
    </div>
  );
}
